import { defineStore, Pinia } from 'pinia'

export enum CustomerType {
  Customer = 'Campsite customer',
  SiteOwner = 'Campsite owner',
  Other = 'other',
}

export const CustomerCancelReason = 'Cancel/amend booking'

export const CustomerReasons = [
  CustomerCancelReason,
  'Deposit refund - have new booking ID',
  'Deposit refund - general query',
  'Problem',
  'Question',
  'Suggestion',
]

export const SiteOwnerReasons = [
  'Problem',
  'Question',
  'Suggestion',
  'Propose new campsite',
]

export const OtherReasons = [
  'Problem',
  'Question',
  'Suggestion',
  'Propose new campsite',
]

export const useContactStore = defineStore('contactStore', () => {
  const reason = ref<string>('')
  const formSubmitted = ref(false)
  const route = useRoute()

  const customerType = computed(() => {
    switch (route.params.customerType) {
      case 'customer':
        return CustomerType.Customer
      case 'manager':
        return CustomerType.SiteOwner
      case 'other':
        return CustomerType.Other
      default:
        return null
    }
  })

  function updateReason(newReason: string) {
    reason.value = newReason
  }

  function setFormSubmitted(value: boolean) {
    formSubmitted.value = value
  }

  const isCustomerTypeStage = computed(() => {
    return customerType.value === null
  })

  const isCustomerTypeCustomer = computed(() => {
    return customerType.value === CustomerType.Customer
  })

  const isCustomerTypeSiteOwner = computed(() => {
    return customerType.value === CustomerType.SiteOwner
  })

  const isCustomerTypeOther = computed(() => {
    return customerType.value === CustomerType.Other
  })

  const isReasonSelectStage = computed(() => {
    return customerType.value !== null && !reason.value
  })

  const isContactFormStage = computed(() => {
    return customerType.value !== null && reason.value
  })

  const shouldShowCustomerCancelWarning = computed(() => {
    return isCustomerTypeCustomer.value && reason.value === CustomerCancelReason
  })

  const shouldShowContactForm = computed(() => {
    return isContactFormStage.value && reason.value !== CustomerCancelReason
  })

  const currentReasons = computed(() => {
    if (customerType.value === null) {
      return []
    }
    if (isCustomerTypeCustomer.value) {
      return CustomerReasons
    }
    if (isCustomerTypeSiteOwner.value) {
      return SiteOwnerReasons
    }
    if (isCustomerTypeOther.value) {
      return OtherReasons
    }
    return []
  })

  const customerTypeTextValue = computed(() => {
    if (customerType.value === null) {
      return ''
    }
    return customerType.value
  })

  return {
    customerType,
    reason,
    updateReason,
    formSubmitted,
    setFormSubmitted,
    isCustomerTypeStage,
    isCustomerTypeCustomer,
    isCustomerTypeSiteOwner,
    isCustomerTypeOther,
    isReasonSelectStage,
    isContactFormStage,
    shouldShowCustomerCancelWarning,
    shouldShowContactForm,
    currentReasons,
    customerTypeTextValue,
  }
})

export function useContactStoreSetup(pinia: Pinia) {
  const contactStore = useContactStore(pinia)
  return contactStore
}
